import { getArticleIdentityKey, InventoryArticle, MkmProductInventoryMap } from 'shared'

export interface IdentityDelta {
  identityKey: string
  delta: number
}

// Sum of `location.quantity` across every inventory article in `inventoryMap` that
// shares both the given mkmProductId and the given identity (same condition / language /
// finish / signed / first-ed / country / grading attributes). Identities never cross
// MKM products, so this is the only slice that matters for the reserved-quantity
// invariant of one SKU.
//
// Articles with no `ptArticleId` are skipped — they're staged-but-not-saved and so
// don't contribute to the on-disk total the invariant is defined against.
export const getStockedQuantityForIdentity = (
  inventoryMap: MkmProductInventoryMap,
  mkmProductId: number,
  identityKey: string
): number => {
  const articles = inventoryMap[mkmProductId] ?? []
  let total = 0
  for (const article of articles) {
    if (!article.ptArticleId) continue
    if (getArticleIdentityKey(article) !== identityKey) continue
    for (const location of article.locations ?? []) total += location.quantity
  }
  return total
}

// Reserved quantity of the saved pt_article that matches this identity within the given
// MKM product, or 0 when no such pt_article is present in the inventory map. Used when a
// staged article (panel split, bulk condition edit) needs to inherit the *real* reserved
// for its new SKU rather than the source article's (a `_.cloneDeep` quirk) or zero.
export const getReservedQuantityForIdentity = (
  inventoryMap: MkmProductInventoryMap,
  mkmProductId: number,
  identityKey: string
): number => {
  const articles = inventoryMap[mkmProductId] ?? []
  for (const article of articles) {
    if (!article.ptArticleId) continue
    if (getArticleIdentityKey(article) !== identityKey) continue
    return Number(article.ptArticleReservedQuantity ?? 0)
  }
  return 0
}

// True if applying the proposed deltas would leave the article's identity below its
// reserved quantity. Other identities (split-off pt_articles, non-matching new rows)
// have no DB row yet so their reserved is 0 — they can't violate the invariant on their
// own.
export const wouldGoBelowReserved = (
  inventoryMap: MkmProductInventoryMap,
  article: InventoryArticle,
  deltas: IdentityDelta[]
): boolean => {
  // No DB row → no real reservation; treat as zero.
  if (!article.ptArticleId) return false
  const reserved = Number(article.ptArticleReservedQuantity ?? 0)
  if (reserved <= 0) return false
  const articleKey = getArticleIdentityKey(article)
  const stocked = getStockedQuantityForIdentity(
    inventoryMap,
    article.card._id as number,
    articleKey
  )
  let totalDelta = 0
  for (const { identityKey, delta } of deltas) {
    if (identityKey === articleKey) totalDelta += delta
  }
  return stocked + totalDelta < reserved
}
